// BALANCE SHEET v2 — batch 5's rebuild of BalanceSheet (UI rebuild; drawn when lib/ui-v2.ts switches batch 5 on).
//
// Astra's screen (02-screen-review/balance-sheet) under the operator's rulings of 26 Oct 2026: the title with ONE as-of
// stamp, the reports' tabs, the position first — Assets / Liabilities / Equity — then one row per section (its figure,
// its stamp, its valuation state), a section's lines behind its row, and the rates, stated prices and notes behind
// "Basis & supporting detail". Read-only: no button but Try again.
//
// WHETHER IT CLOSES leads the page. A sheet that balances says so in the stamp line ("Balances"); one that does not, or
// could not be checked (`closes` false either way — unverified is not a pass), is the red line under the tabs, naming
// the asset it could not value or the gap with its unit. An unvalued figure is "—" with "XRP not valued" beside it,
// never a zero. A failed refresh keeps the figures with the failure above them and claims no "Balances".
//
// KEPT FROM TODAY'S SCREEN (screens/BalanceSheet.tsx; its rules copied into components/v2/reports-bs/sheet.tsx): the
// query and its key, the as-of stamp, the proof line's two sentences, the green word only when the Worker flags no gap,
// "—" for never posted and for unvalued, the unvalued assets by name (from the note and the parts), "Part estimate" on
// a line, the native amounts under each line, the position's parts (muted, never re-added), each line's caveat, the
// rate warning's short sentence with both rates and their unit, a warning naming a figure kept in view, the rates with
// their sample and spread, the cross rate's derivation, the pegs, the stated prices with source, date and age, and the
// unread stated-price table as a failed read with Try again. RESTORED 1 Nov 2026 (his "restore most the lost things"): the
// Net worth figure, the strip's last cell, and the rates' medians as one quiet line under the sections. NOT KEPT: the date
// choice Astra draws — the route reads the
// sheet as of now only (`/api/balance-sheet` takes no date); a position at a chosen date is Mechanisms' to add.
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import type { BalanceSheetSection } from '../../lib/api';
import { readState } from '../../lib/read-state';
import { dubaiDate, fmtDay, fmtRate, fmtTime } from '../../lib/format';
import { Pill } from '../../components/kit';
import { EmptyState, Notice, PageHead, Panel, ReadFailed, Register, SkeletonRows, SummaryStrip, StateLine, type RegisterColumn, ReadNotice } from '../../components/v2/kit';
import { ReportTabs } from '../../components/v2/reports-bs/ReportTabs';
import {
  Aed, Basis, ProofLine, SectionDetail, StatedUnread, pinnedWarnings, sectionState, sectionSummary, shortWarning, unvaluedAssets,
} from '../../components/v2/reports-bs/sheet';

export function BalanceSheetV2() {
  const q = useQuery({ queryKey: ['balance-sheet'], queryFn: () => api.balanceSheet() });
  const state = readState(q);
  const retry = () => { void q.refetch(); };

  // THE OPEN SECTION lives in the URL: Back closes it.
  const [params, setParams] = useSearchParams();
  const openKey = params.get('section');
  const open = useCallback((s: BalanceSheetSection) => setParams((p) => { const n = new URLSearchParams(p); n.set('section', s.key); return n; }), [setParams]);
  const close = useCallback(() => setParams((p) => { const n = new URLSearchParams(p); n.delete('section'); return n; }, { replace: true }), [setParams]);

  const data = q.data;
  const known = state === 'known';
  // The all-clear is a word beside the stamp, and only for a read that succeeded, holds something, closes, and that the
  // Worker itself does not flag with a gap (one under a dirham, which the check calls balanced).
  const posted = !!data && data.sections.some((s) => s.lines.some((l) => l.posted));
  const clear = known && posted && !!data && data.check.closes && !data.notes.some((n) => n.code === 'presentation_gap');
  const stamp = data && (
    <>
      As of {fmtDay(data.as_of, dubaiDate(data.as_of).slice(0, 4) !== dubaiDate().slice(0, 4))} · {fmtTime(data.as_of)}
      {state === 'failed' && ' · old read'}
      {clear && <span className="ml-2 align-middle"><Pill tone="good">Balances</Pill></span>}
    </>
  );

  const head = (
    <>
      <PageHead title="Balance sheet" sub={stamp || 'Position at an instant'} />
      <ReportTabs current="/balance-sheet" />
    </>
  );

  if (state === 'failed' && !data) {
    return <>{head}<ReadNotice what="the balance sheet" error={q.error} onRetry={retry} /></>;
  }
  if (!data) {
    return (
      <>
        {head}
        {q.fetchStatus === 'paused' && <Notice tone="neutral">Offline — waiting to read.</Notice>}
        <Panel flush><SkeletonRows rows={3} what="the balance sheet" /></Panel>
      </>
    );
  }

  const unvalued = unvaluedAssets(data);
  const sections = data.sections;
  const pinned = pinnedWarnings(data);
  const opened = openKey ? sections.find((s) => s.key === openKey) ?? null : null;

  const value = (s: BalanceSheetSection) => <Aed value={s.total_aed} unit />;
  // ONE READ, ONE INSTANT: every section is as of the stamp under the title, so no row repeats it (Astra, 1 Nov 2026).
  // A section short of a value says "Not valued"; WHICH asset is said once, in the red line above (Astra, 1 Nov 2026).
  const stateOf = (s: BalanceSheetSection) => {
    const st = sectionState(s, unvalued);
    return st ? <span className={`text-meta ${st.tone === 'warn' ? 'text-warn' : 'text-tg-hint'}`}>{st.tone === 'warn' ? 'Not valued' : st.words}</span> : null;
  };
  const columns: RegisterColumn<BalanceSheetSection>[] = [
    { key: 'section', head: 'Section', width: 'w-[40%]', cell: (s) => (
      <>
        <span className="block font-medium">{s.label}</span>
        <span className="mt-0.5 block truncate text-meta text-tg-hint">{sectionSummary(s)}</span>
      </>
    ) },
    { key: 'value', head: 'Value', align: 'right', cell: value },
    { key: 'state', head: 'State', cell: (s) => stateOf(s) ?? (s.lines.some((l) => l.posted) ? <span className="text-meta text-tg-hint">Valued</span> : null) },
  ];

  return (
    <>
      {head}
      {/* A failed refresh: the figures stay, the failure above them, no "Balances" (the stamp says "old read"). */}
      {state === 'failed' && <Notice tone="warn"><ReadFailed what="the balance sheet" error={q.error} onRetry={retry} /></Notice>}
      {!data.check.closes && (
        <div className="mb-4 rounded-lg border border-neg/25 bg-neg-bg px-4 py-3 lg:mb-5"><ProofLine data={data} unvalued={unvalued} /></div>
      )}
      {data.rates.stated_available === false && (
        <div className="mb-4 rounded-lg border border-warn/25 bg-warn-bg px-4 py-3 lg:mb-5"><StatedUnread onRetry={retry} /></div>
      )}

      {!posted ? (
        known && <Panel><EmptyState>Nothing posted yet.</EmptyState></Panel>
      ) : (
        <>
          {/* NET WORTH, the one figure up top (the old screen's header; his "restore most the lost things", 1 Nov 2026) — the
              sections are the table's, never said twice (Astra, 1 Nov 2026). */}
          <SummaryStrip phoneCols={1} items={[{ label: 'Net worth', value: <Aed value={data.net_assets_aed} unit /> }]} />
          <Panel flush>
            <Register label="Balance sheet sections" columns={columns} rows={sections} rowKey={(s) => s.key} onOpen={open}
              card={(s) => ({
                title: <><span className="block">{s.label}</span><span className="mt-0.5 block text-meta font-normal text-tg-hint">{sectionSummary(s)}</span></>,
                figure: value(s),
                state: stateOf(s),
              })} />
            <div className="flex items-start justify-between gap-4 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
              {/* THE RATES IN VIEW (the old screen's Rates card): a small table, a pair a row with its rate on the right (the
                  audit, 1 Nov 2026: two rates and their pairs ran together in one "AED at …" line). Spread and sample stay in Basis. */}
              {data.rates.pairs.length > 0 ? (
                <dl className="min-w-0" aria-label="Valuation rates">
                  <dt className="mb-1 text-label font-medium uppercase tracking-wider">Valued in AED at</dt>
                  {data.rates.pairs.map((p) => (
                    <dd key={p.pair} className="grid grid-cols-[6rem_auto] gap-x-4">
                      <span>{p.pair}</span><span className="text-right font-mono tabular text-tg-text">{fmtRate(p.median, p.pair.split('/')[1])}</span>
                    </dd>
                  ))}
                </dl>
              ) : <span className="min-w-0">AED derived from desk rates</span>}
              <span className="shrink-0 whitespace-nowrap">Dubai time</span>
            </div>
          </Panel>
          {pinned.length > 0 && (
            <div className="mt-4 space-y-2 lg:mt-5">
              {pinned.map((n) => (
                <StateLine key={n.code + n.message.slice(0, 12)} tone="warn" more={n.message}>{shortWarning(n)}</StateLine>
              ))}
            </div>
          )}
          <Basis data={data} />
        </>
      )}

      {opened && <SectionDetail key={opened.key} section={opened} unvalued={unvalued} onClose={close} />}
    </>
  );
}
